import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLayers, FiChevronDown, FiChevronRight, FiPlus } from 'react-icons/fi';
import { Node, Edge } from 'reactflow';
import { fabricTemplates, TemplateLevel } from '../../Config/fabric/fabricTemplates';

interface Pattern {
  id: string;
  name: string;
  description: string;
  category: string;
  level?: TemplateLevel;
  nodes: Node[];
  edges: Edge[];
  icon: string;
}

interface PatternsLibraryProps {
  onApplyPattern: (pattern: Pattern) => void;
  onOpenWizard?: () => void;
  open?: boolean;
  onClose?: () => void;
}

const LEVEL_COLORS: Record<TemplateLevel, { bg: string; text: string; border: string }> = {
  'Starter':        { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
  'Intermédiaire':  { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
  'Production':     { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
  'Enterprise':     { bg: '#F3E8FF', text: '#6B21A8', border: '#C084FC' },
};

const PatternsLibrary: React.FC<PatternsLibraryProps> = ({ onApplyPattern, onOpenWizard, open, onClose }) => {
  const { t } = useTranslation('canvas');
  // Support controlled (open/onClose) or uncontrolled mode
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? open! : internalOpen;
  const [expandedCategory, setExpandedCategory] = useState<string | null>('Microsoft Fabric');
  const [activeLevel, setActiveLevel] = useState<TemplateLevel | 'Tous'>('Tous');

  // Azure patterns (static)
  const azurePatterns: Pattern[] = [
    {
      id: 'load-balancer-vms',
      name: 'Load Balancer + VMs',
      description: 'Configuration classique avec load balancer et plusieurs VMs',
      category: 'Azure',
      icon: '⚖️',
      nodes: [
        { id: 'lb-1', type: 'custom', position: { x: 200, y: 50 }, data: { type: 'azurerm_lb', label: 'Load Balancer', icon: '', params: {} } },
        { id: 'vm-1', type: 'custom', position: { x: 100, y: 200 }, data: { type: 'azurerm_virtual_machine', label: 'VM 1', icon: '', params: {} } },
        { id: 'vm-2', type: 'custom', position: { x: 300, y: 200 }, data: { type: 'azurerm_virtual_machine', label: 'VM 2', icon: '', params: {} } },
      ] as Node[],
      edges: [
        { id: 'e1', source: 'lb-1', target: 'vm-1', type: 'step' },
        { id: 'e2', source: 'lb-1', target: 'vm-2', type: 'step' },
      ] as Edge[],
    },
    {
      id: 'app-db',
      name: 'App + Database',
      description: 'Application web avec base de données',
      category: 'Azure',
      icon: '🗄️',
      nodes: [
        { id: 'app-1', type: 'custom', position: { x: 100, y: 100 }, data: { type: 'azurerm_app_service', label: 'App Service', icon: '', params: {} } },
        { id: 'db-1', type: 'custom', position: { x: 300, y: 100 }, data: { type: 'azurerm_mssql_database', label: 'SQL Database', icon: '', params: {} } },
      ] as Node[],
      edges: [{ id: 'e1', source: 'app-1', target: 'db-1', type: 'step' }] as Edge[],
    },
  ];

  // Convert Fabric templates to Pattern format
  const fabricPatterns: Pattern[] = fabricTemplates.map(t => ({
    ...t,
    category: 'Microsoft Fabric',
    nodes: t.nodes as Node[],
    edges: t.edges as Edge[],
  }));

  const allPatterns = [...azurePatterns, ...fabricPatterns];
  const categories = Array.from(new Set(allPatterns.map(p => p.category)));

  const filteredFabric = activeLevel === 'Tous'
    ? fabricPatterns
    : fabricPatterns.filter(p => p.level === activeLevel);

  const levels: Array<TemplateLevel | 'Tous'> = ['Tous', 'Starter', 'Intermédiaire', 'Production', 'Enterprise'];

  return (
    <div style={{
      position: 'fixed',
      top: 195,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 380,
      background: 'white',
      borderRadius: 8,
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      zIndex: 9999,
      maxHeight: 'calc(100vh - 210px)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
        background: '#F9FAFB',
        borderRadius: '8px 8px 0 0',
      }}>
        <FiLayers size={18} style={{ color: '#038387', flexShrink: 0 }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Templates</span>
        <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 'auto' }}>
          {fabricTemplates.length} Fabric
        </span>
        {(isControlled ? onClose : undefined) && (
          <button
            onClick={isControlled ? onClose : () => setInternalOpen(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#9CA3AF', fontSize: 16, lineHeight: 1 }}
          >×</button>
        )}
      </div>

      {isOpen && (
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {categories.map(category => (
            <div key={category}>
              {/* Category header */}
              <div
                onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  cursor: 'pointer',
                  background: expandedCategory === category ? '#F9FAFB' : 'transparent',
                  borderBottom: '1px solid #F3F4F6',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                {expandedCategory === category ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                {category === 'Microsoft Fabric' && (
                  <span style={{ fontSize: 16, lineHeight: 1 }}>🔷</span>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{category}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF' }}>
                  {allPatterns.filter(p => p.category === category).length}
                </span>
              </div>

              {expandedCategory === category && (
                <div style={{ padding: '8px 12px' }}>

                  {/* Level filter for Fabric */}
                  {category === 'Microsoft Fabric' && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {levels.map(lvl => {
                        const colors = lvl === 'Tous' ? null : LEVEL_COLORS[lvl as TemplateLevel];
                        const isActive = activeLevel === lvl;
                        return (
                          <button
                            key={lvl}
                            onClick={() => setActiveLevel(lvl)}
                            style={{
                              padding: '3px 8px',
                              fontSize: 11,
                              fontWeight: isActive ? 600 : 400,
                              border: `1px solid ${isActive && colors ? colors.border : '#E5E7EB'}`,
                              borderRadius: 12,
                              cursor: 'pointer',
                              background: isActive && colors ? colors.bg : isActive ? '#F3F4F6' : 'white',
                              color: isActive && colors ? colors.text : '#6B7280',
                              transition: 'all 0.15s',
                            }}
                          >
                            {lvl}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Wizard Multi-Domain — Fabric only */}
                  {category === 'Microsoft Fabric' && onOpenWizard && (
                    <div
                      onClick={() => {
                        onOpenWizard();
                        if (isControlled && onClose) onClose();
                        else setInternalOpen(false);
                      }}
                      style={{
                        padding: '10px 12px', marginBottom: 8,
                        border: '2px dashed #C084FC', borderRadius: 8,
                        cursor: 'pointer', background: '#FAF5FF', transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLDivElement).style.background = '#F3E8FF';
                        (e.currentTarget as HTMLDivElement).style.borderColor = '#9333EA';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLDivElement).style.background = '#FAF5FF';
                        (e.currentTarget as HTMLDivElement).style.borderColor = '#C084FC';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 20 }}>🧩</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', flex: 1 }}>
                          Configurer Multi-Domain…
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                          background: '#EDE9FE', color: '#7C3AED', border: '1px solid #C084FC',
                        }}>
                          Wizard
                        </span>
                      </div>
                      <p style={{ fontSize: 11, color: '#7C3AED', margin: 0, lineHeight: 1.5, opacity: 0.8 }}>
                        Créez un template personnalisé par domaines métier, workspaces et items en 3 étapes.
                      </p>
                    </div>
                  )}

                  {/* Template cards */}
                  {(category === 'Microsoft Fabric' ? filteredFabric : azurePatterns).map(pattern => {
                    const levelColors = pattern.level ? LEVEL_COLORS[pattern.level] : null;
                    return (
                      <div
                        key={pattern.id}
                        onClick={() => {
                          onApplyPattern(pattern);
                          if (isControlled && onClose) onClose();
                          else setInternalOpen(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          marginBottom: 8,
                          border: '1px solid #E5E7EB',
                          borderRadius: 8,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          background: 'white',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLDivElement).style.background = '#F9FAFB';
                          (e.currentTarget as HTMLDivElement).style.borderColor = '#038387';
                          (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 4px rgba(3,131,135,0.15)';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLDivElement).style.background = 'white';
                          (e.currentTarget as HTMLDivElement).style.borderColor = '#E5E7EB';
                          (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{pattern.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                {pattern.name}
                              </span>
                              {levelColors && (
                                <span style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  padding: '1px 6px',
                                  borderRadius: 10,
                                  background: levelColors.bg,
                                  color: levelColors.text,
                                  border: `1px solid ${levelColors.border}`,
                                  flexShrink: 0,
                                }}>
                                  {pattern.level}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <p style={{ fontSize: 11, color: '#6B7280', margin: '0 0 8px', lineHeight: 1.5 }}>
                          {pattern.description}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#9CA3AF' }}>
                          <FiPlus size={12} />
                          <span>{pattern.nodes.length} ressource{pattern.nodes.length > 1 ? 's' : ''}</span>
                          {pattern.edges.length > 0 && (
                            <span style={{ marginLeft: 6 }}>· {pattern.edges.length} connexion{pattern.edges.length > 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatternsLibrary;
